import type { CellMetrics } from "./types.ts";

/** Round to nearest integer, ties away from zero (per specs/cell-model.md). */
export function roundHalfAwayFromZero(value: number): number {
  const rounded = value >= 0 ? Math.floor(value + 0.5) : -Math.floor(-value + 0.5);
  return rounded || 0; // normalize -0 → 0
}

/** A computed px value in cells on the spacing scale (1 cell = 0.25rem). */
export function pxInCells(px: number, rootFontSizePx: number): number {
  return rootFontSizePx > 0 ? px / (0.25 * rootFontSizePx) : 0;
}

/** Convert a computed px value to whole cells on the spacing scale. */
export function pxToCells(px: number, rootFontSizePx: number): number {
  return roundHalfAwayFromZero(pxInCells(px, rootFontSizePx));
}

/** Convert a percentage of an integer container to whole cells, ties away from zero. */
export function percentToCells(percent: number, containerCells: number): number {
  return roundHalfAwayFromZero((containerCells * percent) / 100);
}

/** `px` up to the next whole 1/64 px, the layout unit Chromium and
 * WebKit snap boxes to (text staying in floats). */
export function roundUpToLayoutUnit(px: number): number {
  return Math.ceil(px * 64 - 1e-6) / 64;
}

/** How Typed OM reads an `auto` minimum, a per-engine constant read once
 * off a probe (see measureCellMetrics). */
let autoMinimum: string | undefined;

/** Measure the root's cell from the host's PERSISTENT light-DOM probe (100
 * "M"s inheriting the host's font): the advance of a monospace character
 * (with the root's own letter-spacing) and the line-box height. Root
 * leading/tracking thus size the grid; descendants' are quantized to it
 * (specs/cell-model.md). The probe must be long-lived: a throwaway node
 * created at measure time can transiently resolve the FALLBACK font even
 * after the real font has loaded, whereas a persistent node is
 * re-font-matched by the same machinery as real content. */
export function measureCellMetrics(host: HTMLElement, probe: HTMLElement): CellMetrics {
  const rect = probe.getBoundingClientRect();
  const letterSpacing = parseFloat(getComputedStyle(host).letterSpacing) || 0;
  // Glyph ink vs line box: a Range rect spans the font's ascent + descent,
  // which some fonts draw TALLER than their `normal` line box. WebKit
  // fragments columns at ink bottoms, so multicol needs the overhang.
  const range = probe.ownerDocument.createRange();
  range.selectNodeContents(probe);
  const contentHeight = range.getBoundingClientRect().height;
  const inkOverhang = Math.max(0, contentHeight - rect.height);
  // The reverse: a content area SHORTER than the line box (WebKit's SF
  // Mono, 16.5px in 17) is all an inline background covers, so rows of
  // background would show a hairline between them.
  const backgroundGap = Math.max(0, rect.height - contentHeight);
  // The cell is the advance rounded up to a layout unit; the grid's
  // letter-spacing carries the remainder and the light DOM keeps its
  // natural advance (specs/cell-model.md "Typography").
  const measured = rect.width / 100;
  const width = roundUpToLayoutUnit(measured);
  // The probe's mark — an empty inline-block the host appends last —
  // sits on the baseline.
  const mark = probe.lastElementChild;
  const baseline = mark ? mark.getBoundingClientRect().top - rect.top : undefined;
  // The probe holds its minimum at `auto`, whatever the page's CSS says.
  autoMinimum ??=
    "computedStyleMap" in probe ? probe.computedStyleMap().get("min-width")?.toString() : undefined;
  return {
    width,
    advance: measured,
    height: rect.height,
    letterSpacing,
    gridLetterSpacing: letterSpacing + (width - measured),
    inkOverhang,
    backgroundGap,
    ...(baseline === undefined ? {} : { baseline }),
    ...(autoMinimum === undefined ? {} : { autoMinimum }),
  };
}

/** Whether two measurements agree on every metric: any change rewrites
 * the host's properties. */
export function sameMetrics(a: CellMetrics, b: CellMetrics): boolean {
  const keys = Object.keys(a) as (keyof CellMetrics)[];
  return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key]);
}

export function getRootFontSizePx(): number {
  return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
}
